import React from 'react';
import { Users, DollarSign, CreditCard, X, Clock } from 'lucide-react';
import { Button } from '../common/Button';
import { ModalPortal } from '../common/ModalPortal';
import { PayoutInfo, AmbassadorEarnings } from '../../services/referralService';
import referralService from '../../services/referralService';

interface AmbassadorPaymentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  earnings: AmbassadorEarnings;
  payouts: PayoutInfo[];
  zIndex?: number;
}

const AmbassadorPaymentsModal: React.FC<AmbassadorPaymentsModalProps> = ({
  isOpen,
  onClose,
  earnings,
  payouts,
  zIndex = 10000
}) => {
  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div 
        className="fixed inset-0 flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm overflow-auto"
        style={{ zIndex }}
      >
        <div className="w-full max-w-5xl bg-background-light dark:bg-background-dark rounded-xl border border-border-light dark:border-border-dark shadow-xl animate-modal-in overflow-hidden">
          <div className="sticky top-0 z-10 flex items-center justify-between p-4 md:p-6 border-b border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark">
            <h3 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
              Ambassador Payments & Earnings
            </h3>
            <button 
              onClick={onClose}
              className="p-1 rounded-md text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-surface-dark transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="p-4 md:p-6">
            {/* Summary cards */}
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="w-full p-4 bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-green-500/10 dark:bg-green-500/10">
                    <DollarSign className="w-5 h-5 text-green-500 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Total Earnings</p>
                    <p className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
                      {referralService.formatCurrency(earnings.totalEarnings)}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="w-full p-4 bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-500/10 dark:bg-blue-500/10">
                    <Clock className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Pending Earnings</p>
                    <p className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
                      {referralService.formatCurrency(earnings.pendingEarnings)}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="w-full p-4 bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-purple-500/10 dark:bg-purple-500/10">
                    <CreditCard className="w-5 h-5 text-purple-500 dark:text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Total Payouts</p>
                    <p className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
                      {referralService.formatCurrency(earnings.totalPayouts)}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="w-full p-4 bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-500/10 dark:bg-amber-500/10">
                    <Users className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Successful Referrals</p>
                    <p className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
                      {earnings.successfulReferrals}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Payout history table */}
            <div className="mb-6">
              <h4 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark mb-4">
                Recent Payouts
              </h4>
              
              {payouts.length > 0 ? (
                <div className="w-full overflow-x-auto rounded-lg border border-border-light dark:border-border-dark">
                  <table className="w-full">
                    <thead className="bg-surface-light dark:bg-surface-dark">
                      <tr>
                        <th className="p-3 text-left text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Date</th>
                        <th className="p-3 text-left text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Amount</th>
                        <th className="p-3 text-left text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Status</th>
                        <th className="p-3 text-left text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Transaction ID</th>
                        <th className="p-3 text-left text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">UTR</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-light dark:divide-border-dark">
                      {payouts.map((payout) => (
                        <tr key={payout.id} className="hover:bg-surface-light/50 dark:hover:bg-surface-dark/50 transition-colors">
                          <td className="p-3 text-sm text-text-primary-light dark:text-text-primary-dark">
                            {referralService.formatTimestamp(payout.created_at)}
                          </td>
                          <td className="p-3 text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                            {referralService.formatCurrency(payout.amount)}
                          </td>
                          <td className="p-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${referralService.getPayoutStatusColor(payout.status)}`}>
                              {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-text-secondary-light dark:text-text-secondary-dark font-mono">
                            {payout.reference_id || '-'}
                          </td>
                          <td className="p-3 text-sm text-text-secondary-light dark:text-text-secondary-dark font-mono">
                            {payout.utr || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="p-3 rounded-full bg-surface-light dark:bg-surface-dark inline-flex items-center justify-center mb-3">
                    <CreditCard className="w-6 h-6 text-text-secondary-light dark:text-text-secondary-dark" />
                  </div>
                  <p className="text-text-secondary-light dark:text-text-secondary-dark mb-2 font-heading font-semibold">No payouts yet</p>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    Your payouts will appear here once you start earning from referrals
                  </p>
                </div>
              )}
            </div>
            
            {/* Additional info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
              <h5 className="font-medium text-blue-800 dark:text-blue-300 mb-2">Payment Information</h5>
              <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                <li>• Payouts are processed automatically every 15 days</li>
                <li>• Minimum payout amount is ₹1,000</li>
                <li>• Payments are sent directly to your registered bank account</li>
                <li>• Processing time is typically 1-2 business days</li>
              </ul>
            </div>
            
            <div className="w-full flex justify-end">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default AmbassadorPaymentsModal; 